export interface PlatformBrand {
  /** FontAwesome brand class for the platform's real logo. */
  icon: string;
  label: string;
  /** Official brand color, used for logos and audience-split segments. */
  color: string;
  /** Optional solid gradient (used for the Instagram mark and segments). */
  gradient?: string;
}

/**
 * Real social-platform brand marks and colors. Logos are the one place in the
 * design system allowed to sit outside the palette, so they live here as a
 * single source of truth shared by PlatformIcon and the audience-split bar. Icons are Remix Icon
 * brand marks (loaded in index.html); Font Awesome is not loaded, so `fa-*` classes would render blank.
 */
const PLATFORM_BRANDS: Record<string, PlatformBrand> = {
  tiktok: { icon: 'ri-tiktok-fill', label: 'TikTok', color: '#010101' },
  instagram: {
    icon: 'ri-instagram-fill',
    label: 'Instagram',
    color: '#E1306C',
    gradient: 'linear-gradient(45deg, #F58529 0%, #DD2A7B 45%, #8134AF 75%, #515BD4 100%)',
  },
  youtube: { icon: 'ri-youtube-fill', label: 'YouTube', color: '#FF0000' },
  x: { icon: 'ri-twitter-x-fill', label: 'X', color: '#0F0F0F' },
  twitter: { icon: 'ri-twitter-x-fill', label: 'X', color: '#0F0F0F' },
  facebook: { icon: 'ri-facebook-circle-fill', label: 'Facebook', color: '#1877F2' },
  linkedin: { icon: 'ri-linkedin-box-fill', label: 'LinkedIn', color: '#0A66C2' },
  snapchat: { icon: 'ri-snapchat-fill', label: 'Snapchat', color: '#E8B500' },
  pinterest: { icon: 'ri-pinterest-fill', label: 'Pinterest', color: '#E60023' },
  threads: { icon: 'ri-threads-fill', label: 'Threads', color: '#000000' },
  twitch: { icon: 'ri-twitch-fill', label: 'Twitch', color: '#9146FF' },
  whatsapp: { icon: 'ri-whatsapp-fill', label: 'WhatsApp', color: '#25D366' },
  reddit: { icon: 'ri-reddit-fill', label: 'Reddit', color: '#FF4500' },
  telegram: { icon: 'ri-telegram-fill', label: 'Telegram', color: '#229ED9' },
};

const NEUTRAL_COLOR = '#9CA3AF';

export function getPlatformBrand(platform: string): PlatformBrand | null {
  return PLATFORM_BRANDS[platform.trim().toLowerCase()] ?? null;
}

/** Solid swatch color for a platform, with a neutral fallback for unknown ones. */
export function getPlatformColor(platform: string): string {
  return getPlatformBrand(platform)?.color ?? NEUTRAL_COLOR;
}

export function getPlatformLabel(platform: string): string {
  return getPlatformBrand(platform)?.label ?? platform;
}